import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';

import { type RecentBuilding } from '~/common/types/building';
import { type RouteParams } from '@/types';

import BuildingContext from './BuildingContext';
import { localStorageKeys } from '@/shared/utils/constants';
import { trpc } from '@/config/trpc';

import useScopedLocalStorageUtils from '@/shared/hooks/localStorage/useScopedLocalStorageUtils';

const queryOptions = {
  trpc: {
    context: {
      skipBatch: true,
    },
  },
};
const MAX_RECENT_BUILDINGS_LENGTH = 5;

const createNextBuildings = (recentBuildings: RecentBuilding[], toAddBuilding: RecentBuilding) => {
  if (recentBuildings.some((recentBuilding) => recentBuilding.id === toAddBuilding.id)) return null;

  return [toAddBuilding, ...recentBuildings].slice(0, MAX_RECENT_BUILDINGS_LENGTH);
};

type Props = React.PropsWithChildren;

const BuildingContextProvider: React.FC<Props> = ({ children }) => {
  const { buildingId } = useParams() as RouteParams;

  const recentBuildingsStore = useScopedLocalStorageUtils<RecentBuilding[]>(
    localStorageKeys.recentBuildings,
  );
  const summaryQuery = trpc.building.summaryById.useQuery(buildingId, queryOptions);
  const trpcUtils = trpc.useUtils();

  const queries = {
    hoa: trpc.hoa.summary.useQuery(
      { id: summaryQuery.data?.homeownerAssociationId as string },
      { ...queryOptions, enabled: !!summaryQuery.data?.homeownerAssociationId },
    ),
    pushNotificationSettings:
      trpc.messaging.pushNotificationSettings.getBuildingPortalPushNotificationSettings.useQuery(
        undefined,
        queryOptions,
      ),
    relations: trpc.building.relations.useQuery({ buildingId }, queryOptions),
    summary: summaryQuery,
    tags: trpc.building.tag.listForBuilding.useQuery({ buildingId }, queryOptions),
    units: trpc.building.unit.list.useQuery({ buildingId }, queryOptions),
  };

  useEffect(() => {
    const { homeownerAssociationId, name } = queries.summary.data ?? {};

    if (!buildingId || !name) return;

    const storedBuildings = recentBuildingsStore.get() ?? [];

    const nextBuildings = createNextBuildings(storedBuildings, {
      homeownerAssociationId,
      id: buildingId,
      name,
    });

    if (nextBuildings) recentBuildingsStore.set(nextBuildings);
  }, [buildingId, queries.summary.data, recentBuildingsStore]);

  const invalidateUnits = useCallback(() => {
    trpcUtils.building.unit.list.invalidate({ buildingId });
  }, [buildingId, trpcUtils]);

  const invalidateRelations = useCallback(() => {
    trpcUtils.building.relations.invalidate({ buildingId });
  }, [buildingId, trpcUtils]);

  const relations = useMemo(() => queries.relations.data ?? [], [queries.relations.data]);
  const tags = useMemo(() => queries.tags.data ?? [], [queries.tags.data]);
  const units = useMemo(() => queries.units.data ?? [], [queries.units.data]);

  const value = useMemo(
    () => ({
      buildingSummary: queries.summary.data,
      buildingSummaryIsLoading: queries.summary.isLoading,
      homeownerAssociation: queries.hoa.data,
      invalidateRelations,
      invalidateUnits,
      pushNotificationSettings: queries.pushNotificationSettings.data,
      relations,
      relationsAreLoading: queries.relations.isLoading,
      tags,
      tagsAreLoading: queries.tags.isLoading,
      units,
      unitsAreLoading: queries.units.isLoading,
    }),
    [
      invalidateRelations,
      invalidateUnits,
      queries.hoa.data,
      queries.pushNotificationSettings.data,
      queries.relations.isLoading,
      queries.summary.data,
      queries.summary.isLoading,
      queries.tags.isLoading,
      queries.units.isLoading,
      relations,
      tags,
      units,
    ],
  );

  return <BuildingContext.Provider value={value}>{children}</BuildingContext.Provider>;
};

export default BuildingContextProvider;
